import Autocomplete from '@mui/material/Autocomplete'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import { useJsApiLoader } from '@react-google-maps/api'
import React, { useState, useEffect, useRef } from 'react'

import AppConfig from '@src/config/AppConfig'

declare global {
  interface Window {
    google?: {
      maps?: typeof google.maps
    }
  }
}

interface PlacePrediction {
  description: string
  place_id: string
  lat?: number
  long?: number
}

interface PlaceSelection {
  addr1: string
  city: string
  state: string
  zip: string
  lat: string
  country: string
  long: string
}

interface CustomProps {
  inputProps: Omit<TextFieldProps, 'onChange' | 'onBlur' | 'onFocus' | 'value'>
  value: string | null
  onInputChange: (value: string) => void
  onPlaceSelect: (place: PlaceSelection | null) => void
}

const GooglePlacesAutocomplete: React.FC<CustomProps> = ({
  inputProps,
  value,
  onInputChange,
  onPlaceSelect,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: AppConfig.GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  })
  const [options, setOptions] = useState<PlacePrediction[]>([])
  const requestRef = useRef<NodeJS.Timeout | null>(null)
  const autoCompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null)
  const placesService = useRef<google.maps.places.PlacesService | null>(null)

  useEffect(() => {
    if (isLoaded) {
      autoCompleteService.current =
        new window.google.maps.places.AutocompleteService()
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div'),
      )
    }
  }, [isLoaded])

  useEffect(() => {
    if (
      value &&
      autoCompleteService.current &&
      placesService.current &&
      !requestRef.current
    ) {
      requestRef.current = setTimeout(() => {
        autoCompleteService.current?.getPlacePredictions(
          {
            input: value,
            types: ['geocode'],
          },
          (predictions: PlacePrediction[] | null, status: string) => {
            requestRef.current = null
            if (status !== 'OK' || !predictions) {
              console.error(status)
              return
            }
            setOptions(predictions)
          },
        )
      }, 500)
    } else {
      setOptions([])
    }
  }, [value])

  return (
    <Autocomplete
      popupIcon={null}
      id="google-places-autocomplete"
      getOptionLabel={(option) => option.description}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      inputValue={value ?? ''}
      onInputChange={(_, newInputValue, reason) => {
        if (reason !== 'reset') {
          onInputChange(newInputValue)
        }
      }}
      onChange={(_, place: PlacePrediction | null) => {
        if (place && placesService.current) {
          placesService.current?.getDetails(
            {
              placeId: place.place_id,
              fields: ['address_components,geometry'],
            },
            (result: google.maps.places.PlaceResult | null, status: string) => {
              if (status === 'OK') {
                const address = {
                  addr1: '',
                  addr2: '',
                  city: '',
                  state: '',
                  zip: '',
                  country: '',
                  lat: result?.geometry?.location?.lat().toString() || '',
                  long: result?.geometry?.location?.lng().toString() || '',
                }

                result?.address_components?.forEach((component) => {
                  if (component.types.includes('street_number')) {
                    address.addr1 += `${component.short_name} `
                  } else if (component.types.includes('route')) {
                    address.addr1 += component.short_name
                  } else if (
                    component.types.includes('neighborhood') ||
                    component.types.includes('sublocality')
                  ) {
                    address.addr2 += component.short_name
                  } else if (component.types.includes('locality')) {
                    address.city = component.short_name
                  } else if (
                    component.types.includes('administrative_area_level_1')
                  ) {
                    address.state = component.short_name
                  } else if (component.types.includes('country')) {
                    address.country = component.short_name
                  } else if (component.types.includes('postal_code')) {
                    address.zip = component.short_name
                  }
                })
                onPlaceSelect(address)
              } else {
                console.error('Error fetching place details:', status)
              }
            },
          )
        }
      }}
      renderInput={(params) => <TextField {...params} {...inputProps} />}
    />
  )
}

export default GooglePlacesAutocomplete
